import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import ScheduleIcon from '@/svg-icons/schedule';
import { useUser } from '@/contexts/UserContext';

// A simple module-level cache to prevent fetching inspectors redundantly on the same session
let cachedInspectors = null;
let isFetchingInspectors = false;
let inspectorsFetchPromise = null;

const fetchInspectors = async () => {
  if (cachedInspectors) return cachedInspectors;
  if (isFetchingInspectors && inspectorsFetchPromise) return inspectorsFetchPromise;

  isFetchingInspectors = true;
  inspectorsFetchPromise = API.get('/assignables')
    .then(res => {
      cachedInspectors = res.data;
      return res.data;
    })
    .finally(() => {
      isFetchingInspectors = false;
    });

  return inspectorsFetchPromise;
};

const DraftScheduleSection = ({ orderId }) => {
  const user = useUser();
  const [draftData, setDraftData] = useState(null);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFranchisee = user?.authRole === 'ROLE_FRANCHISEE';

  useEffect(() => {
    if (!isFranchisee) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const loadDraftAndInspectors = async () => {
      try {
        const fetchDraft = API.get(`/scheduler/assignable-orders/${orderId}/draft`);
        const fetchInsp = fetchInspectors();
        const [draftRes, inspectorsRes] = await Promise.all([fetchDraft, fetchInsp]);

        if (isMounted) {
          if (draftRes.status === 200) setDraftData(draftRes.data);
          setInspectors(inspectorsRes || []);
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Error fetching draft schedule or inspectors:', error);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (orderId) loadDraftAndInspectors();
    return () => { isMounted = false; };
  }, [orderId, isFranchisee]);

  if (!isFranchisee || loading || !draftData) return null;

  const { schedule: scheduleInfo = {}, sender: senderInfo = {} } = draftData;
  const { when: scheduleWhen = {}, flextime: isFlextime, observation, who: scheduleWho = {} } = scheduleInfo;
  
  const scheduledDate = scheduleWhen.date;
  const startTime = scheduleWhen.start_time;
  const endTime = scheduleWhen.end_time;
  const assignedInspectorId = scheduleWho.inspector_id;

  const inspector = inspectors.find(insp => insp.public_id === assignedInspectorId);
  const inspectorName = inspector ? inspector.name : 'Desconhecido';

  const toDate = value => (typeof value === 'string' ? parseISO(value) : value);
  const formattedScheduledDate = scheduledDate ? format(toDate(scheduledDate), 'dd/MM/yyyy') : '';
  const formattedSenderWhen = senderInfo.when ? format(toDate(senderInfo.when), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '';

  return (
    <div className="w-full">
      <div className="font-bold text-[calc(6px+0.4vw)] my-[0.6vw] flex items-center">
        <ScheduleIcon className="text-gray-700 mr-2 w-6 h-6 shrink-0" />
        <FormattedMessage id="draftSchedule" defaultMessage="Agendamento em Rascunho" />
      </div>

      <div className="flex flex-wrap text-[#858585] text-[calc(6px+0.4vw)] my-[1vw] pl-[calc(14px+1.8vw)]">
        <div className="w-1/2 mb-[1vw]" data-cy="draft-inspector">
          <div className="font-bold">
            <FormattedMessage id="inspector" defaultMessage="Vistoriador" />
          </div>
          {inspectorName}
        </div>

        <div className="w-1/2 mb-[1vw]" data-cy="draft-assigned-by">
          <div className="font-bold">
            <FormattedMessage id="assignedBy" defaultMessage="Atribuído por" />
          </div>
          {senderInfo.who || 'Sistema'}
          {formattedSenderWhen && ` em ${formattedSenderWhen}`}
        </div>

        <div className="w-1/2 mb-[1vw]" data-cy="draft-scheduled-date">
          <div className="font-bold">
            <FormattedMessage id="scheduledDate" defaultMessage="Data Agendada" />
          </div>
          {formattedScheduledDate} {startTime} - {endTime}
        </div>

        <div className="w-1/2 mb-[1vw]" data-cy="draft-flextime">
          <div className="font-bold">
            <FormattedMessage id="flextime" defaultMessage="Horário Flexível" />
          </div>
          {isFlextime ? <FormattedMessage id="yes" defaultMessage="Sim" /> : <FormattedMessage id="no" defaultMessage="Não" />}
        </div>
        
        {observation && (
          <div className="w-full" data-cy="draft-observations">
            <div className="font-bold mt-[0.5vw]">
              <FormattedMessage id="observations" defaultMessage="Observações" />
            </div>
            <div>
              {observation}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

DraftScheduleSection.propTypes = {
  orderId: PropTypes.string.isRequired,
};

export default DraftScheduleSection;
